import { Group, Switch } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { DateCell, Missing, TextCell } from "@tfg/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import type { FindFeatureFlagsResponseItemDto } from "~/gen/api"

type UseColumnsParams = {
  updatingFeatureFlagName: string | null
  onToggleState: (featureFlag: FindFeatureFlagsResponseItemDto) => void
}

const columnHelper = createColumnHelper<FindFeatureFlagsResponseItemDto>()

const formatBooleanValue = (value: boolean): string => {
  return value ? "true" : "false"
}

export const useColumns = ({
  updatingFeatureFlagName,
  onToggleState,
}: UseColumnsParams): ColumnDef<FindFeatureFlagsResponseItemDto, any>[] => {
  const { t } = useTranslation("admin")

  return useMemo<ColumnDef<FindFeatureFlagsResponseItemDto, any>[]>(
    () => [
      columnHelper.accessor("name", {
        header: t("featureFlags.columns.name"),
        enableSorting: false,
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor("description", {
        header: t("featureFlags.columns.description"),
        enableSorting: false,
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor("category", {
        header: t("featureFlags.columns.category"),
        enableSorting: false,
        cell: ({ getValue }) => <TextCell value={getValue()} />,
      }),
      columnHelper.accessor("ttlDays", {
        header: t("featureFlags.columns.ttlDays"),
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue()

          return value !== null ? <TextCell value={value} /> : <Missing />
        },
      }),
      columnHelper.accessor("defaultState", {
        header: t("featureFlags.columns.defaultState"),
        enableSorting: false,
        cell: ({ getValue }) => (
          <TextCell value={formatBooleanValue(getValue())} />
        ),
      }),
      columnHelper.accessor("createdAt", {
        header: t("featureFlags.columns.createdAt"),
        enableSorting: false,
        cell: ({ getValue }) => <DateCell date={getValue()} format="lll" />,
      }),
      columnHelper.accessor("updatedAt", {
        header: t("featureFlags.columns.updatedAt"),
        enableSorting: false,
        cell: ({ getValue }) => <DateCell date={getValue()} format="lll" />,
      }),
      columnHelper.accessor("state", {
        header: t("featureFlags.columns.state"),
        enableSorting: false,
        cell: ({ row: { original } }) => {
          const isUpdating = updatingFeatureFlagName === original.name

          return (
            <Group>
              <Switch
                checked={original.state}
                onChange={() => onToggleState(original)}
                disabled={isUpdating}
              />
            </Group>
          )
        },
      }),
    ],
    [onToggleState, t, updatingFeatureFlagName]
  )
}
